/**
 * Toast Manager
 * Imperative API for managing toast notifications
 */

import type { GenericToastUpdate, Toast, ToastOptions } from './types';
import type { TradeStatusUpdate } from '@/types/api/quickTrade';
import { TERMINAL_STATUSES } from '@/types/api/quickTrade';

type ToastListener = (toasts: Toast[]) => void;

class ToastManager {
  private toasts: Map<string, Toast> = new Map();
  private listeners: Set<ToastListener> = new Set();
  private autoCloseTimers: Map<string, NodeJS.Timeout> = new Map();

  /**
   * Subscribe to toast changes
   */
  subscribe(listener: ToastListener): () => void {
    this.listeners.add(listener);
    // Immediately notify with current state
    listener(Array.from(this.toasts.values()));

    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners of toast changes
   */
  private notify(): void {
    const toastArray = Array.from(this.toasts.values());
    this.listeners.forEach((listener) => listener(toastArray));
  }

  /**
   * Create a new toast notification
   */
  createToast(options: ToastOptions): string {
    const idBase = options.tradeId ?? `generic-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const toastKind = options.kind || 'trade';
    const id = `toast-${idBase}-${Date.now()}`;
    const toast: Toast = {
      id,
      kind: toastKind,
      tradeId: options.tradeId,
      tokenSymbol: options.tokenSymbol,
      tokenAddress: options.tokenAddress,
      tokenLogo: options.tokenLogo,
      tradeType: options.tradeType,
      status: options.status || (toastKind === 'trade' ? 'queued' : undefined),
      title: options.title,
      variant: options.variant,
      message: options.message,
      txHash: options.txHash,
      chainId: options.chainId,
      strategyName: options.strategyName,
      actionLabel: options.actionLabel,
      onClick: options.onClick,
      autoCloseMs: options.autoCloseMs,
      createdAt: Date.now(),
    };

    this.toasts.set(id, toast);
    this.notify();

    if (toast.kind === 'generic' && toast.autoCloseMs !== 0) {
      this.scheduleAutoClose(id, toast.autoCloseMs ?? 8000);
    } else if (toast.status && TERMINAL_STATUSES.includes(toast.status)) {
      this.scheduleAutoClose(id);
    }

    return id;
  }

  /**
   * Update an existing toast
   */
  updateToast(id: string, update: TradeStatusUpdate): void {
    const toast = this.toasts.get(id);
    if (!toast) return;

    if (toast.kind !== 'trade') return;

    // Update toast with new data
    const updatedToast: Toast = {
      ...toast,
      status: update.status,
      message: update.message || toast.message,
      txHash: update.tx_hash || toast.txHash,
    };

    this.toasts.set(id, updatedToast);
    this.notify();

    // Schedule auto-close for terminal statuses
    if (TERMINAL_STATUSES.includes(update.status)) {
      this.scheduleAutoClose(id);
    }
  }

  /**
   * Update a generic toast without coercing it through trade-status fields.
   */
  updateGenericToast(id: string, update: GenericToastUpdate): void {
    const toast = this.toasts.get(id);
    if (!toast || toast.kind !== 'generic') return;

    this.toasts.set(id, { ...toast, ...update });
    this.notify();

    if (update.autoCloseMs !== undefined) {
      if (update.autoCloseMs === 0) {
        this.clearAutoCloseTimer(id);
      } else {
        this.scheduleAutoClose(id, update.autoCloseMs);
      }
    }
  }

  /**
   * Enrich an existing toast with token metadata (symbol, logo)
   */
  enrichToast(id: string, fields: { tokenSymbol?: string; tokenLogo?: string | null }): void {
    const toast = this.toasts.get(id);
    if (!toast) return;

    this.toasts.set(id, { ...toast, ...fields });
    this.notify();
  }

  /**
   * Remove a toast
   */
  removeToast(id: string): void {
    this.toasts.delete(id);
    this.clearAutoCloseTimer(id);
    this.notify();
  }

  /**
   * Find toast by trade ID
   */
  findToastByTradeId(tradeId: number): Toast | undefined {
    return Array.from(this.toasts.values()).find((toast) => toast.tradeId === tradeId);
  }

  /**
   * Schedule auto-close for a toast (5 seconds for trade toasts)
   */
  private scheduleAutoClose(id: string, durationMs = 5000): void {
    // Clear existing timer if any
    this.clearAutoCloseTimer(id);

    // Set new timer
    const timer = setTimeout(() => {
      this.removeToast(id);
    }, durationMs);

    this.autoCloseTimers.set(id, timer);
  }

  /**
   * Clear auto-close timer for a toast
   */
  private clearAutoCloseTimer(id: string): void {
    const timer = this.autoCloseTimers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.autoCloseTimers.delete(id);
    }
  }

  /**
   * Clear all toasts
   */
  clearAll(): void {
    this.toasts.clear();
    this.autoCloseTimers.forEach((timer) => clearTimeout(timer));
    this.autoCloseTimers.clear();
    this.notify();
  }
}

// Singleton instance
export const toastManager = new ToastManager();
